import { useTranslations } from 'next-intl';
import type { FC, PropsWithChildren } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineHelp } from '@/app/components/icons/BaseLineHelp';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Popover, PopoverAnchor, PopoverClose, PopoverContent } from '@/app/components/ui/popover';
import { toast } from '@/app/components/ui/use-toast';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormTabType, RecaptionPrompt } from '@/app/types';
import { REQUIRED_KEYS } from '../../constants';
import { PromptLibraryOption, PromptLibraryOptionType } from './PromptLibraryOption';

export const PromptLibraryPopover: FC<
  PropsWithChildren<{
    open: boolean;
    onOpenChange?: (open: boolean) => void;
    taskType: Exclude<FormTabType, 'scene'>;
    shouldUpgrade?: boolean;
    onOpenPlanDialog?: () => void;
  }>
> = ({ open, onOpenChange, taskType, children, shouldUpgrade, onOpenPlanDialog }) => {
  const t = useTranslations('create.proMode');

  const { formParams, updateFormParams } = useStore((state) => ({
    formParams: state[`${taskType}FormParams`],
    updateFormParams: state.updateFormParams,
  }));

  const { recaptionPrompt } = formParams;
  const store = useStoreApi();

  const updateRecaptionPrompt = (newRecaptionPrompt: Partial<RecaptionPrompt>) => {
    // 局部更新
    const draft = {
      ...store.getState()[`${taskType}FormParams`].recaptionPrompt,
      ...newRecaptionPrompt,
    } as RecaptionPrompt;
    updateFormParams(taskType, 'recaptionPrompt', draft);
  };

  const promptKeysConfig = [
    {
      title: t('style.title'),
      key: 'style' as const,
      type: PromptLibraryOptionType.Multiple,
      options: [
        t('style.realistic'),
        t('style.animation'),
        t('style.3d'),
        t('style.pixelArt'),
        t('style.inkWash'),
        t('style.lineArt'),
        t('style.oilPainting'),
      ],
      hasAddButton: true,
      tip: t('bestNumber', { number: '1-3' }),
    },
    {
      title: t('motionLevel.title'),
      key: 'motionLevel' as const,
      type: PromptLibraryOptionType.Single,
      options: [t('motionLevel.small'), t('motionLevel.middle'), t('motionLevel.large')],
      hasAddButton: false,
      tip: t('bestNumber', { number: '1' }),
    },
    {
      title: t('shotSize.title'),
      key: 'shotSize' as const,
      type: PromptLibraryOptionType.Single,
      options: [
        t('shotSize.extremelyWide'),
        t('shotSize.wide'),
        t('shotSize.medium'),
        t('shotSize.closeUp'),
        t('shotSize.extremelyCloseUp'),
      ],
      hasAddButton: true,
      tip: t('bestNumber', { number: '0-1' }),
    },
    {
      title: t('focalLength.title'),
      key: 'focalLength' as const,
      type: PromptLibraryOptionType.Single,
      options: [t('focalLength.wide'), t('focalLength.standard'), t('focalLength.telephoto'), t('focalLength.fisheye')],
      hasAddButton: false,
      tip: t('bestNumber', { number: '0-1' }),
    },
    {
      title: t('composition.title'),
      key: 'composition' as const,
      type: PromptLibraryOptionType.Single,
      options: [
        t('composition.diagonal'),
        t('composition.ruleOfThirds'),
        t('composition.symmetrical'),
        t('composition.overShoulder'),
        t('composition.dutchAngle'),
      ],
      hasAddButton: true,
      tip: t('bestNumber', { number: '0-1' }),
    },
    {
      title: t('cameraAngle.title'),
      key: 'cameraAngle' as const,
      type: PromptLibraryOptionType.Single,
      options: [
        t('cameraAngle.low'),
        t('cameraAngle.high'),
        t('cameraAngle.eyeLevel'),
        t('cameraAngle.birdEye'),
        t('cameraAngle.snorriCam'),
      ],
      hasAddButton: false,
      tip: t('bestNumber', { number: '0-1' }),
    },
    {
      title: t('cameraMovement.title'),
      key: 'cameraMovement' as const,
      type: PromptLibraryOptionType.Multiple,
      options: [
        t('cameraMovement.trackLeft'),
        t('cameraMovement.panLeft'),
        t('cameraMovement.trackRight'),
        t('cameraMovement.panRight'),
        t('cameraMovement.moveUp'),
        t('cameraMovement.moveDown'),
        t('cameraMovement.tiltUp'),
        t('cameraMovement.tiltDown'),
        t('cameraMovement.zoomIn'),
        t('cameraMovement.zoomOut'),
        t('cameraMovement.clockwise'),
        t('cameraMovement.counterclockwise'),
        t('cameraMovement.static'),
      ],
      hasAddButton: true,
      tip: t('bestNumber', { number: '0-2' }),
    },
    {
      title: t('specialFilmingTechnique.title'),
      key: 'specialFilmingTechnique' as const,
      type: PromptLibraryOptionType.Single,
      options: [
        t('specialFilmingTechnique.timeLapse'),
        t('specialFilmingTechnique.macro'),
        t('specialFilmingTechnique.fpv'),
        t('specialFilmingTechnique.aerial'),
      ],
      hasAddButton: true,
      tip: t('bestNumber', { number: '0-1' }),
    },
  ];
  const updatePromptValues = (key: keyof RecaptionPrompt, value: string[]) => {
    // 检查是否允许：
    if (REQUIRED_KEYS.includes(key) && value.length === 0) {
      toast({
        message: t('minSelect', { length: 1 }),
        type: 'error',
      });
      return;
    }

    updateRecaptionPrompt({ [key]: value });
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={12}
        collisionPadding={{
          top: 80,
        }}
        className="max-h-[80vh] w-[456px] space-y-4 overflow-y-auto bg-system-bg02 px-4 py-3 backdrop-blur-[60px] lg:w-[456px]"
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement;
          const isTagOrAddButton = target.closest('[data-tag-group]') !== null;
          if (isTagOrAddButton) {
            e.preventDefault();
          }
        }}
      >
        {promptKeysConfig.map((group) => (
          <PromptLibraryOption
            key={group.key}
            title={group.title}
            groupKey={group.key}
            options={group.options}
            value={recaptionPrompt?.[group.key] || []}
            onValueChange={updatePromptValues}
            required={REQUIRED_KEYS.includes(group.key)}
            tip={group.tip}
            hasAddButton={group.hasAddButton}
            shouldUpgrade={shouldUpgrade}
            onOpenPlanDialog={onOpenPlanDialog}
          />
        ))}
        <CustomTooltip
          content={t.rich('allBestNumber', {
            p: (chunks) => <p className="mb-2 text-system-white64">{chunks}</p>,
            ul: (chunks) => <ul className="mb-0 list-disc space-y-2 pl-4 marker:text-system-blue02">{chunks}</ul>,
            li: (chunks) => <li className="mb-0">{chunks}</li>,
          })}
          contentClassName="text-xs"
          trigger="click"
        >
          <BaseLineHelp className="absolute top-0 right-12 text-system-white" />
        </CustomTooltip>
        <PopoverClose asChild>
          <button type="button" className="absolute top-0 right-4 text-system-white" aria-label="Close">
            <BaseLineClose className="text-base" />
          </button>
        </PopoverClose>
      </PopoverContent>
    </Popover>
  );
};
